'use client';

import { Box, Typography, LinearProgress } from '@mui/material';
import { useTranslations } from 'next-intl';

export type PasswordStrength = 'weak' | 'medium' | 'strong';

export interface PasswordStrengthResult {
  strength: PasswordStrength;
  score: number;
  checks: {
    minLength: boolean;
    hasLower: boolean;
    hasUpper: boolean;
    hasNumber: boolean;
  };
}

export function calculatePasswordStrength(password: string): PasswordStrengthResult {
  const checks = {
    minLength: password.length >= 8,
    hasLower: /[a-z]/.test(password),
    hasUpper: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };

  const passedChecks = Object.values(checks).filter(Boolean).length;
  const score = (passedChecks / 4) * 100;

  let strength: PasswordStrength;
  if (passedChecks <= 2) {
    strength = 'weak';
  } else if (passedChecks === 3) {
    strength = 'medium';
  } else {
    strength = 'strong';
  }

  return { strength, score, checks };
}

interface PasswordStrengthIndicatorProps {
  password: string;
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const t = useTranslations('creator.auth.passwordStrength');
  const { strength, score, checks } = calculatePasswordStrength(password);

  if (!password) {
    return null;
  }

  const colorMap: Record<PasswordStrength, 'error' | 'warning' | 'success'> = {
    weak: 'error',
    medium: 'warning',
    strong: 'success',
  };

  // Build list of missing criteria for non-strong passwords
  const missingCriteria: string[] = [];
  if (!checks.minLength) missingCriteria.push(t('needsLength'));
  if (!checks.hasLower) missingCriteria.push(t('needsLower'));
  if (!checks.hasUpper) missingCriteria.push(t('needsUpper'));
  if (!checks.hasNumber) missingCriteria.push(t('needsNumber'));

  return (
    <Box sx={{ mt: 0.5, mb: 1 }}>
      <LinearProgress
        variant="determinate"
        value={score}
        color={colorMap[strength]}
        sx={{ height: 4, borderRadius: 2 }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0.5 }}>
        <Typography variant="caption" color={`${colorMap[strength]}.main`}>
          {t(strength)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', maxWidth: '70%' }}>
          {strength === 'strong' ? t('requirements') : missingCriteria.join(', ')}
        </Typography>
      </Box>
    </Box>
  );
}
